import React, { useCallback, useState } from 'react'
import { ButtonPrimary, ButtonSecondary } from '../../Button'
import { ComponentWithPermissions, UserPermissions } from '../../CreateRoutering/types'
import LoaderDecider from '../../LoaderDecider'
import { ModalMedium } from '../../Modal'
import { ModalOpenedTy } from '../../Modal/types'
import PermissionChecker from '../../PermissionChecker'
import VerticalSortList from '../VerticalSortList'
import { TableInstanceType } from '../../TwinTable/types'
import TwinTrans from '../../TwinTrans'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { VerticalSortItem } from '../VerticalSortList/types'

type ButtonModalSortListStateLessProps = ComponentWithPermissions & ButtonModalStateLessLogicProps & {
    data: VerticalSortItem[] | null
    setData: React.Dispatch<React.SetStateAction<VerticalSortItem[] | null>>
}

export const ButtonModalSortListStateLess: React.FC<ButtonModalSortListStateLessProps> = ({ userPermissions, data, setData, updateOrder, onOpen }) => {
    const { opened, setOpened, myUpdateOrder, onMyOpen } = useButtonModalStateLessLogic({ updateOrder, onOpen })
    return (
        <PermissionChecker permission='update' userPermissions={userPermissions}>
            <ButtonSecondary type='button' onClick={onMyOpen}>
                <TwinTrans transKey={'updateOrder'}>Actualizar orden</TwinTrans>
            </ButtonSecondary>
            {opened && <ModalMedium opened={true} setOpened={setOpened} className='flex flex-col'>
                <h2 className='mb-35'>
                    <TwinTrans transKey='updatePriorityOrder'>Actualizar el orden de prioridad</TwinTrans>
                </h2>
                <LoaderDecider loading={data === null}>
                    {data !== null && <VerticalSortList items={data as any} setItems={setData}></VerticalSortList>}
                </LoaderDecider>
                <ButtonPrimary type='button' className='mt-30 ml-auto' onClick={myUpdateOrder}>
                    <TwinTrans transKey='save'>Guardar</TwinTrans>
                </ButtonPrimary>
            </ModalMedium>}
        </PermissionChecker>
    )
}

interface ButtonModalStateLessLogicProps {
    updateOrder: () => Promise<void>
    onOpen?: () => void
}

const useButtonModalStateLessLogic = ({ updateOrder, onOpen }: ButtonModalStateLessLogicProps) => {
    const [opened, setOpened] = useState<ModalOpenedTy>(null)

    const myUpdateOrder = useCallback(async () => {
        await updateOrder()
        setOpened(null)
    }, [updateOrder])

    const onMyOpen = useCallback(() => {
        setOpened(true)
        onOpen?.()
    }, [onOpen])

    return { opened, setOpened, myUpdateOrder, onMyOpen }
}

export type ItemSortFunction = (data: VerticalSortItem[]) => Promise<boolean>

interface ButtonModalSortListStateFullProps extends ButtonModalSortListStateFullLogicProps {
    userPermissions: UserPermissions
}

const ButtonModalSortListStateFull: React.FC<ButtonModalSortListStateFullProps> = ({ userPermissions, ...rest}) => {
    const { getData, ...logic } = useButtonModalSortListStateFullLogic({...rest})
    return (
        <ButtonModalSortListStateLess userPermissions={userPermissions} {...logic} onOpen={getData}/>
    )
}

interface ButtonModalSortListStateFullLogicProps {
    getDataFrom: string
    updateData: ItemSortFunction
    tableInstance: TableInstanceType
    extraWhereParams?: TwinDictionary
    parseDataFnc?: (data: TwinDictionary[]) => TwinDictionary[]
}

const useButtonModalSortListStateFullLogic = ({ tableInstance, getDataFrom, updateData, extraWhereParams, parseDataFnc }: ButtonModalSortListStateFullLogicProps) => {
    const [data, setData] = useState<VerticalSortItem[] | null>(null)
    const extraWhereStr = JSON.stringify(extraWhereParams)
    const getData = useCallback(async () => {
        const extraWhereJS = extraWhereStr ? JSON.parse(extraWhereStr) : undefined
        const mydata = await twinFetchPostJSON(getDataFrom, { order: [['order', 'ASC']], where: extraWhereJS })
        if (mydata) {
            let myDataParsed = mydata
            if (parseDataFnc) {
                myDataParsed = parseDataFnc(myDataParsed)
            }
            setData(myDataParsed)
        }
    }, [getDataFrom, extraWhereStr, parseDataFnc])

    const updateOrder = useCallback(async () => {
        if (data && await updateData(data)) {
            tableInstance?.getTableDataFromStart()
        }
    }, [data, tableInstance, updateData])

    return { data, setData, updateOrder, getData }
}

export default ButtonModalSortListStateFull